import {useContext} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Box} from '@mui/material';

import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {CultureAgent} from '../../culture-agent';
import type {CultureAgentError} from '../../culture-agent';

// In development use relative URLs so the dev server proxy (setupProxy.js) forwards
// to REACT_APP_CULTURE_AGENT_API_HOST. In production use the real API host.
const API_HOST = process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_CULTURE_AGENT_API_HOST ?? '';
const API_KEY = process.env.REACT_APP_CULTURE_AGENT_API_KEY ?? '';

export const CultureAgentChat = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const language = useContext(LanguageContext);

  const searchParams = new URLSearchParams(window.location.search);
  const isScreeningSurvey = searchParams.get('source') === 'screening_applicant';

  const handleSessionObtained = (sessionId: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set('session_id', sessionId);
    navigate(`/talent/survey?${params.toString()}`, {replace: true});
  };

  const handleSurveyComplete = (sessionId: string, certainty: number) => {
    navigate('/talent/survey_results', {
      state: {params: {sessionId, isScreeningSurvey, certainty}},
    });
  };

  const handleError = (error: CultureAgentError) => {
    console.error('[CultureAgent]', error.code, error.message);
  };

  return (
    <Box
      sx={{
        height: {xs: '60vh'},
        '@supports (height: 60dvh)': {height: {xs: '60dvh'}},
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      <Box sx={{flex: 1, maxWidth: {md: 1000}, width: '100%', mx: 'auto'}}>
        <CultureAgent
          config={{apiHost: API_HOST, apiKey: API_KEY, passThreshold: 80}}
          language={language}
          labels={{
            agentName: t('talents.chatBasedSurvey.agentName'),
            agentTitle: t('talents.chatBasedSurvey.agentTitle'),
            agentStatusLabel: t('talents.chatBasedSurvey.agentStatusLabel'),
            initialMessage: t('talents.chatBasedSurvey.initialMessage'),
            instructions: t('talents.chatBasedSurvey.instructions'),
            readinessMessage: t('talents.chatBasedSurvey.readinessMessage'),
            readinessYesLabel: t('talents.chatBasedSurvey.readinessYesLabel'),
            readinessNoLabel: t('talents.chatBasedSurvey.readinessNoLabel'),
            notReadyMessage: t('talents.chatBasedSurvey.notReadyMessage'),
            refreshToRestartHint: t('talents.chatBasedSurvey.refreshToRestartHint'),
            inputPlaceholder: t('talents.chatBasedSurvey.inputPlaceholder'),
            seeResults: t('talents.chatBasedSurvey.seeResults'),
            resultsHint: t('talents.chatBasedSurvey.resultsHintMessage'),
            progressDescription: t('talents.chatBasedSurvey.progressDescription'),
          }}
          onSessionObtained={handleSessionObtained}
          onSurveyComplete={handleSurveyComplete}
          onError={handleError}
        />
      </Box>
    </Box>
  );
};
